import React from 'react'
import { Modal, Space } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { Button } from '@surecompanies/core_components'
import isNil from 'lodash/isNil'
import { BulkUploadPreviewStep } from './BulkUploadPreviewStep'
import { BulkUploadResultsStep } from './BulkUploadResultsStep'
import { BulkUploadSelectStep } from './BulkUploadSelectStep'
import { useBulkUpload } from './useBulkUpload'
import { useBulkUploadColumns } from './useBulkUploadColumns'

interface BulkUploadModalProps {
  open: boolean
  onClose: () => void
}

const BulkUploadModal = ({ open, onClose }: BulkUploadModalProps) => {
  const upload = useBulkUpload(onClose)

  const columns = useBulkUploadColumns({
    clients: upload.clients,
    fieldErrorsByRow: upload.errorIndex.fieldErrorsByRow,
    plansByRow: upload.plansByRow,
    onClientFix: upload.handleClientFix,
    onFieldChange: upload.handleFieldChange,
    onRemoveRow: upload.handleRemoveRow,
  })

  const footer = (
    <Space>
      <Button onClick={upload.handleClose}>Close</Button>
      {upload.step === 'preview' ? (
        <>
          <Button onClick={upload.handleReset} disabled={upload.isUploading}>
            Back
          </Button>
          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={upload.handleUpload}
            loading={upload.isUploading}
            disabled={upload.hasBlockingIssues}
            title={
              upload.hasBlockingIssues
                ? 'Fix the highlighted rows before uploading'
                : undefined
            }
          >
            Confirm
          </Button>
        </>
      ) : null}
    </Space>
  )

  return (
    <Modal
      open={open}
      onCancel={upload.handleClose}
      title="Upload billing profiles"
      footer={footer}
      width={1060}
      style={{ maxWidth: 'calc(100vw - 32px)' }}
      destroyOnClose
    >
      {upload.step === 'select' ? (
        <BulkUploadSelectStep
          fileInputRef={upload.fileInputRef}
          isExporting={upload.isExporting}
          onFileSelect={upload.handleFileSelect}
          onDownloadCurrentValues={() =>
            void upload.handleDownloadCurrentValues()
          }
        />
      ) : null}

      {upload.step === 'preview' ? (
        <BulkUploadPreviewStep
          fileName={upload.selectedFileName}
          rowCount={upload.displayRows.length}
          pagedRows={upload.pagedRows}
          columns={columns}
          errorGroups={upload.errorGroups}
          errorIndex={upload.errorIndex}
          plansByRow={upload.plansByRow}
          planSummary={upload.planSummary}
          isPlanLoading={upload.isPlanLoading}
          isPlanError={upload.isPlanError}
          pageSize={upload.pageSize}
          currentPage={upload.currentPage}
          onPageChange={upload.setCurrentPage}
          onPageSizeChange={upload.handlePageSizeChange}
        />
      ) : null}

      {upload.step === 'results' && !isNil(upload.result) ? (
        <BulkUploadResultsStep result={upload.result} />
      ) : null}
    </Modal>
  )
}

export { BulkUploadModal }
